import { useState } from 'react'

import {
    AccessibleView,
    AccessibleViewList,
    useAccessibleViews,
} from 'v2/views/utils/useAccessibleViews'
import * as Y from 'yjs'

import { useObjects } from 'data/hooks/objects'
import * as yUtils from 'features/utils/yUtils'
import { WidgetAdminControlsProps } from 'features/views/LayoutEditor/types'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import {
    LinkBlocksWidgetLink,
    LinkBlocksWidgetLinkTargetField,
    LinkBlocksWidgetLinkTargetUrl,
    LinkBlocksWidgetLinkTargetView,
    LinkBlocksWidgetType,
} from 'features/views/LayoutEditor/widgets/LinkBlocksWidget/types'
import {
    getLinkBlockTargetType,
    getLinkTargetFields,
} from 'features/views/LayoutEditor/widgets/LinkBlocksWidget/utils'
import { getIsStackerUserObject } from 'features/workspace/stackerUserUtils'

import useDebounce from 'v2/ui/utils/useDebounce'

const DEBOUNCE_RATE = 300 // ms

type TargetLink = NonNullable<LinkBlocksWidgetLink['link']>

type LinkBlocksLinkTargetControlStateProps = {
    link?: LinkBlocksWidgetLink
    value: LinkBlocksWidgetLink['link']
    onChange: WidgetAdminControlsProps<LinkBlocksWidgetType>['onChange']
}

export function useLinkBlocksLinkTargetControlState({
    link: providedLink,
    value,
    onChange,
}: LinkBlocksLinkTargetControlStateProps) {
    const debouncedOnChange = useDebounce(onChange, DEBOUNCE_RATE) as typeof onChange

    const [localLink, setLocalLink] = useState<TargetLink | undefined>(value)

    const onChangeLinkParams = (partial: Partial<TargetLink>) => {
        setLocalLink((prev) => {
            const newValue = {
                ...prev,
                ...partial,
            } as TargetLink

            debouncedOnChange((attrs) => {
                const links = attrs.get('links')
                if (!links) return

                const link = yUtils.arrayFind<Y.Map<any>>(
                    links,
                    (t) => t.get('id') === providedLink?.id
                )
                if (!link) return

                let linkTarget = link.get('link')
                if (!linkTarget) {
                    linkTarget = link.set('link', new Y.Map())
                }

                for (const [key, value] of Object.entries(newValue)) {
                    linkTarget.set(key, value)
                }
            })

            return newValue
        })
    }

    const linkType = getLinkBlockTargetType(providedLink)
    const onChangeLinkType = (value: string) => {
        onChangeLinkParams({ type: value as TargetLink['type'] })
    }

    const linkHref = (localLink as LinkBlocksWidgetLinkTargetUrl)?.href ?? ''
    const onChangeLinkHref = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        onChangeLinkParams({ href: value })
    }

    const linkLabel = localLink?.label ?? ''
    const onChangeLinkLabel = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        onChangeLinkParams({ label: value })
    }

    const linkOpenAs = (localLink?.openAs ?? 'full_page') === 'new_tab'
    const onChangeLinkOpenAs = (value: boolean) => {
        const newValue = value ? 'new_tab' : 'full_page'
        onChangeLinkParams({ openAs: newValue })
    }

    const linkFieldApiName = (localLink as LinkBlocksWidgetLinkTargetField)?.fieldApiName ?? ''
    const onChangeFieldApiName = (value: string | null) => {
        onChangeLinkParams({ fieldApiName: value ?? '' })
    }

    const { fields } = useLayoutEditorContext()
    const supportedFields = getLinkTargetFields(fields)

    const accessibleViews = useAccessibleViews()
    const { data: objects = [] } = useObjects()

    const internalLinks = getInternalLinks(accessibleViews, objects)
    const onChangeInternalLink = (link: string) => {
        const { viewSid, objectSid } = decodeLinkId(link)

        onChangeLinkParams({
            viewSid: viewSid,
            objectSid: objectSid,
        })
    }
    const { viewSid, objectSid } = (localLink as LinkBlocksWidgetLinkTargetView) ?? {}
    const internalLinksValue = encodeLinkId(viewSid, objectSid)

    return {
        linkType,
        onChangeLinkType,
        linkHref,
        onChangeLinkHref,
        linkLabel,
        onChangeLinkLabel,
        linkOpenAs,
        onChangeLinkOpenAs,
        linkFieldApiName,
        onChangeFieldApiName,
        supportedFields,
        internalLinks,
        onChangeInternalLink,
        internalLinksValue,
    }
}

type InternalLinkView = {
    type: 'view'
    id: string
    viewSid: string
    viewTypeLabel: string
    objectSid: string
    viewType: ViewDto['type']
    label: string
}

type InternalLinkSection = {
    type: 'section'
    id: string
    label: string
    children: InternalLinkView[]
}

function getInternalLinks(views: AccessibleViewList, objects: ObjectDto[]) {
    const objectsBySid = objects.reduce((acc, curr) => {
        return acc.set(curr._sid, curr)
    }, new Map<string, ObjectDto>())

    const { blankpage, list, create } = views

    const internalLinks: InternalLinkSection[] = []

    const blankpageSection: InternalLinkSection = {
        type: 'section',
        id: 'blankpage',
        label: 'Custom pages',
        children: getInternalLinksFromAccessibleViews(blankpage, objectsBySid),
    }
    internalLinks.push(blankpageSection)

    const viewSections = [...list, ...create].reduce((acc, curr) => {
        const objectSid = curr.view.object_id ?? ''
        if (!objectSid) return acc

        const object = objectsBySid.get(objectSid)
        if (!object) return acc

        let section = acc.get(objectSid)
        if (!section) {
            section = {
                type: 'section',
                id: objectSid,
                label: object.name,
                children: [],
            }
            acc.set(objectSid, section)
        }

        if (isAccessibleViewValid(curr, objectsBySid)) {
            section.children.push(getInternalLinkFromAccessibleView(curr))
        }

        return acc
    }, new Map<string, InternalLinkSection>())

    const sortedSections = Array.from(viewSections.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
    )
    internalLinks.push(...sortedSections)

    return internalLinks.filter((s) => s.children.length > 0)
}

function getInternalLinksFromAccessibleViews(
    views: AccessibleView[],
    objects: Map<string, ObjectDto>
): InternalLinkView[] {
    const links = views.reduce<InternalLinkView[]>((acc, curr) => {
        if (!isAccessibleViewValid(curr, objects)) return acc

        const internalLink = getInternalLinkFromAccessibleView(curr)

        return [...acc, internalLink]
    }, [])

    return links.sort((a, b) => a.label.localeCompare(b.label))
}

function getInternalLinkFromAccessibleView(av: AccessibleView): InternalLinkView {
    const objectSid = av.view.object_id ?? ''
    const viewSid = av.view._sid
    const viewType = av.view.type as keyof typeof viewTypeLabelMap

    const label = av.navItem?.label ?? av.view.name ?? ''
    const viewTypeLabel = viewTypeLabelMap[viewType] ?? ''

    return {
        type: 'view',
        id: encodeLinkId(viewSid, objectSid),
        viewSid,
        objectSid,
        viewType: av.view.type,
        viewTypeLabel,
        label,
    }
}

function isAccessibleViewValid(av: AccessibleView, objects: Map<string, ObjectDto>): boolean {
    const { navItem } = av

    // Validate navItem.
    if (navItem?.enabled === false || navItem?.hidden) {
        return false
    }

    const object = objects.get(av.view.object_id ?? '')
    if (object?.connection_options?.is_hidden || getIsStackerUserObject(object)) {
        return false
    }

    return true
}

function encodeLinkId(viewSid: string = '', objectSid: string = '') {
    if (!viewSid && !objectSid) return ''

    return `${objectSid}::${viewSid}`
}

function decodeLinkId(linkId: string) {
    const [objectSid, viewSid] = linkId.split('::')
    return { viewSid, objectSid }
}

const viewTypeLabelMap = {
    list: 'List view',
    create: 'Create view',
    blankpage: 'Custom page',
}
